import React, { useState, useMemo } from "react";
import { RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import { fmtM, resolveF } from "../shared/constants";

const pct  = (v,dec=0) => v==null?"—":`${(+(v||0)*100).toFixed(dec)}%`;
const clrT = (v,lo=0.3,hi=0.6) => !v?"#888":v>=hi?"#16A34A":v>=lo?"#D97706":"#DC2626";
const fmtN = v => v==null||v===0?"—":v.toLocaleString();

const EMP_BUCKETS = [
  { key:"micro",  label:"< 50",    min:0,    max:50,    tamMult:0.6  },
  { key:"small",  label:"50–250",  min:50,   max:250,   tamMult:1.2  },
  { key:"mid",    label:"250–1K",  min:250,  max:1000,  tamMult:2.0  },
  { key:"ent",    label:"1K+",     min:1000, max:Infinity, tamMult:3.5 },
];

const DEAL_BUCKETS = [
  { key:"xs",  label:"<$10K",    min:0,      max:10000   },
  { key:"sm",  label:"$10–50K",  min:10000,  max:50000   },
  { key:"md",  label:"$50–150K", min:50000,  max:150000  },
  { key:"lg",  label:"$150K+",   min:150000, max:Infinity},
];

const COL_TIPS = {
  total:       "Total accounts assigned to this rep in HubSpot",
  touchRate30: "% of accounts with a meaningful touch (call, meeting, or note — not email) in the last 30 days",
  touchRate60: "% of accounts with a meaningful touch (call, meeting, or note — not email) in the last 60 days",
  touchRate90: "% of accounts with a meaningful touch (call, meeting, or note — not email) in the last 90 days",
  openDeals:   "Count of currently open pipeline deals (all open deals, regardless of when created)",
  openPipe:    "Total $ value of all currently open pipeline deals (not year-filtered — shows live pipeline regardless of when created)",
  wonDeals:    "Number of deals closed-won in the selected calendar year (by close date)",
  closedRev:   "Total $ value of deals closed-won in the selected calendar year",
  wr:          "Win rate = Won ÷ (Won + Lost) for deals whose close date falls in the selected year",
  xs:          "# currently open deals under $10K",
  sm:          "# currently open deals $10K–$50K",
  md:          "# currently open deals $50K–$150K",
  lg:          "# currently open deals $150K+",
  estTAM:      "Estimated territory TAM = Plan ADS × company size multiplier (micro 0.6×, small 1.2×, mid 2.0×, enterprise 3.5×)",
  avgScore:    "Marketing Score — average HubSpot contact score (hubspot_score) across all accounts in territory",
  avgContacts: "Avg contacts per account. Accounts with 2+ contacts have a buyer + champion — stronger coverage. Click row to see contact depth breakdown.",
};

// Tooltip — fixed position so it's never clipped by overflow:hidden parents
const Tip = ({ text, children }) => {
  const [pos, setPos] = useState(null);
  const ref = React.useRef(null);
  const show = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({ x: r.left + r.width / 2, y: r.top });
  };
  const hide = () => setPos(null);
  return (
    <div ref={ref} style={{display:"inline-flex",alignItems:"center"}}
      onMouseEnter={show} onMouseLeave={hide}>
      {children}
      {pos && text && (
        <div style={{
          position:"fixed",
          left: pos.x, top: pos.y - 6,
          transform:"translate(-50%, -100%)",
          background:"#1A1A1A",color:"#F8F8F8",fontSize:10,lineHeight:1.5,
          padding:"7px 10px",borderRadius:7,zIndex:99999,
          boxShadow:"0 4px 16px rgba(0,0,0,0.3)",pointerEvents:"none",
          textAlign:"center",fontWeight:400,width:"max-content",maxWidth:260,
        }}>
          {text}
          <div style={{position:"absolute",top:"100%",left:"50%",transform:"translateX(-50%)",
            width:0,height:0,borderLeft:"5px solid transparent",borderRight:"5px solid transparent",
            borderTop:"5px solid #1A1A1A"}}/>
        </div>
      )}
    </div>
  );
};

const LoadingPanel = ({ count=0, loadStart }) => {
  const [elapsed, setElapsed] = React.useState(0);
  React.useEffect(()=>{
    const t=setInterval(()=>setElapsed(loadStart?Math.floor((Date.now()-loadStart)/1000):0),1000);
    return()=>clearInterval(t);
  },[loadStart]);
  const mins=Math.floor(elapsed/60), secs=elapsed%60;
  const rate=elapsed>2?Math.round(count/elapsed):0;
  return (
    <div style={{margin:"32px 0",background:"#fff",border:"1px solid #E8E8E8",borderRadius:12,padding:"28px 32px"}}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
        <div style={{width:10,height:10,borderRadius:"50%",background:"#CC1A1A",animation:"pulse 1.2s ease-in-out infinite"}}/>
        <span style={{fontSize:14,fontWeight:700,color:"#111"}}>Syncing accounts from HubSpot…</span>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
        {[
          {l:"Accounts fetched",v:count.toLocaleString(),          c:"#CC1A1A"},
          {l:"Pages loaded",    v:Math.ceil(count/100).toLocaleString(),c:"#7C3AED"},
          {l:"Elapsed",         v:mins>0?`${mins}m ${secs}s`:`${secs}s`,c:"#D97706"},
          {l:"Accts / sec",     v:rate>0?`~${rate}`:"—",           c:"#16A34A"},
        ].map((s,i)=>(
          <div key={i} style={{background:"#F8F8F8",borderRadius:8,padding:"12px 16px"}}>
            <div style={{fontSize:10,color:"#888",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em",fontWeight:600}}>{s.l}</div>
            <div style={{fontSize:22,fontWeight:800,color:s.c,fontFamily:"'DM Mono',monospace"}}>{s.v}</div>
          </div>
        ))}
      </div>
      <div style={{height:4,background:"#F0F0F0",borderRadius:2,overflow:"hidden"}}>
        <div style={{height:"100%",width:"40%",background:"linear-gradient(90deg,#CC1A1A,#D97706)",
          animation:"indeterminate 1.4s ease-in-out infinite",borderRadius:2}}/>
      </div>
      <style>{`@keyframes indeterminate{0%{transform:translateX(-250%)}100%{transform:translateX(600%)}}@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
      <div style={{fontSize:11,color:"#aaa",marginTop:10}}>
        Filtered to your configured rep territories — should complete in under a minute.
      </div>
    </div>
  );
};

const AccountCoverage = ({
  settings, hsData, companies=[], companiesLoaded,
  companiesLoading, companiesCount, companiesLoadStart, onLoadCompanies
}) => {
  const reps   = (settings?.team?.reps||[]).filter(r=>r.name&&r.hubspotOwnerId);
  const planDS = +(settings?.targets?.avgDealSize||0);
  const planWR = +(settings?.targets?.winRate||0)/100;
  const F      = resolveF(settings);

  const curYear = new Date().getFullYear();
  const [filterYear,  setFilterYear]  = useState(curYear);
  const [expandedRep, setExpandedRep] = useState(null);
  const [sortBy,      setSortBy]      = useState("touchRate30");
  const [sortDir,     setSortDir]     = useState("asc");

  const rawDeals = hsData?.rawDeals    || [];
  const wonDeals = hsData?.rawWonDeals || [];
  const now    = Date.now();
  const msDay  = 86400000;

  // Year filter boundaries
  const yearStart = useMemo(()=>new Date(filterYear, 0, 1).getTime(),            [filterYear]);
  const yearEnd   = useMemo(()=>new Date(filterYear, 11, 31, 23, 59, 59).getTime(),[filterYear]);

  // ── Deal data: classify and index by OWNER ID ──────────────────────────────
  // Do NOT attempt a company join here — HubSpot v3 deals API does not return
  // associations in a properties call. Pipeline/won/WR are keyed by owner directly.
  const dealData = useMemo(()=>{
    const mappings = settings?.stages?.mappings||[];
    const wonIds   = mappings.filter(m=>m.type==="won").map(m=>m.id.toLowerCase());
    const lostIds  = mappings.filter(m=>m.type==="lost").map(m=>m.id.toLowerCase());
    const hasMaps  = mappings.length>0;

    const isWon  = d=>{ const s=(d.properties?.[F.dealstage]||"").toLowerCase(); return wonIds.length?wonIds.includes(s):s.includes("closedwon"); };
    const isLost = d=>{ const s=(d.properties?.[F.dealstage]||"").toLowerCase(); return lostIds.length?lostIds.includes(s):s.includes("closedlost"); };
    const isOpen = d=>!isWon(d)&&!isLost(d);
    const ownOf  = d=>String(d.properties?.[F.owner]||d.properties?.hubspot_owner_id||"");
    const amtOf  = d=>+(d.properties?.[F.amount]||0);
    const tsOf   = str=>str?new Date(str).getTime():0;
    const getCreate = d=>d.properties?.[F.createdate]||d.properties?.createdate||"";
    const getClose  = d=>d.properties?.[F.closedate] ||d.properties?.closedate ||"";

    // Deduplicate across rawDeals + wonDeals
    const dedup=new Map();
    [...rawDeals,...wonDeals].forEach(d=>{ if(!dedup.has(d.id)) dedup.set(d.id,d); });
    const all = Array.from(dedup.values());

    const allWon  = hasMaps ? all.filter(isWon) : wonDeals;
    const allLost = all.filter(isLost);
    const allOpen = rawDeals.filter(isOpen);

    // Year filter logic:
    // - Open deals: NO year filter — show all currently open pipeline regardless of when created.
    //   A deal created in Nov 2025 that's still open in March 2026 IS live pipeline.
    // - Won/Lost: filter by closedate — revenue is recognized when a deal closes.
    const openYear = allOpen; // all currently open deals
    const wonYear  = allWon.filter( d=>{ const t=tsOf(getClose(d));  return t>=yearStart&&t<=yearEnd; });
    const lostYear = allLost.filter(d=>{ const t=tsOf(getClose(d));  return t>=yearStart&&t<=yearEnd; });

    // Index by owner ID — this always works because hubspot_owner_id is on every deal
    const byOwner = {};
    const addTo = (oid, type, d) => {
      if(!oid) return;
      if(!byOwner[oid]) byOwner[oid] = { open:[], won:[], lost:[], wonAllTime:[] };
      byOwner[oid][type].push(d);
    };
    openYear.forEach( d => addTo(ownOf(d), "open",       d));
    wonYear.forEach(  d => addTo(ownOf(d), "won",        d));
    lostYear.forEach( d => addTo(ownOf(d), "lost",       d));
    allWon.forEach(   d => addTo(ownOf(d), "wonAllTime", d)); // for customer flag

    // Pre-compute per-owner deal stats
    const ownerStats = {};
    Object.entries(byOwner).forEach(([oid, deals])=>{
      const openPipe   = deals.open.reduce((a,d)=>a+amtOf(d),0);
      const closedRev  = deals.won.reduce( (a,d)=>a+amtOf(d),0);
      const totCl      = deals.won.length + deals.lost.length;
      const dealBuckets = {};
      DEAL_BUCKETS.forEach(b=>{
        dealBuckets[b.key] = deals.open.filter(d=>{ const a=amtOf(d); return a>=b.min&&a<b.max; }).length;
      });
      ownerStats[oid] = {
        openPipe, closedRev,
        openDeals:  deals.open.length,
        wonDeals:   deals.won.length,
        lostDeals:  deals.lost.length,
        isCustomer: deals.wonAllTime.length > 0,
        wr:         totCl>0 ? deals.won.length/totCl : null,
        dealBuckets,
      };
    });

    return { ownerStats };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[rawDeals, wonDeals, settings?.stages?.mappings, filterYear]);

  // ── Company enrichment — activity + territory only ────────────────────────
  // Companies are used for: account counts, L30/L60/L90 touch rates, dark account
  // lists, TAM estimate, and MES score. Deal numbers come from ownerStats above.
  const repOwnerIds  = useMemo(()=>new Set(reps.map(r=>String(r.hubspotOwnerId))),[reps]);
  const repCompanies = useMemo(()=>companies.filter(c=>repOwnerIds.has(String(c.properties?.hubspot_owner_id||""))),[companies,repOwnerIds]);

  const enriched = useMemo(()=>{
    const cut30=now-30*msDay, cut60=now-60*msDay, cut90=now-90*msDay;
    return repCompanies.map(c=>{
      const p       = c.properties||{};
      const ownerId = String(p.hubspot_owner_id||"");

      // Meaningful touch = call, meeting, or note — NOT email, NOT sequence, NOT field edit.
      // hs_last_sales_activity_type tells us what the last sales activity was.
      // If it's EMAIL or SALES_EMAIL we skip it and look for the next best signal.
      // Falls back to notes_last_contacted (manual contact log) if no qualifying activity.
      const EMAIL_TYPES = new Set(["EMAIL","SALES_EMAIL","FORWARDED_EMAIL","INCOMING_EMAIL"]);
      const lastSalesType = (p.hs_last_sales_activity_type||"").toUpperCase();
      const lastSalesDate = p.hs_last_sales_activity_date
        ? new Date(p.hs_last_sales_activity_date).getTime() : 0;

      // Use sales activity date only if it was a meaningful type (not email)
      const meaningfulSales = lastSalesDate > 0 && !EMAIL_TYPES.has(lastSalesType)
        ? lastSalesDate : 0;

      // notes_last_contacted = manually logged contacts (calls/meetings) — good signal
      const lastContacted = p.notes_last_contacted
        ? new Date(p.notes_last_contacted).getTime() : 0;

      // Take the most recent meaningful signal — never use hs_lastmodifieddate
      const lastAct = Math.max(meaningfulSales, lastContacted);
      const daysIdle  = lastAct ? Math.floor((now-lastAct)/msDay) : 999;
      const employees = +(p.numberofemployees||0);
      const empBucket = EMP_BUCKETS.find(b=>employees>=b.min&&employees<b.max)||EMP_BUCKETS[0];
      const estTAM    = planDS>0 ? planDS*(empBucket.tamMult) : 0;
      const score     = +(p.hubspot_score||p.hubspotscore||p.hs_predictivecontactscore_v2||0);

      // isCustomer from deal data (all-time won by this owner)
      const ds = dealData.ownerStats[ownerId];
      const isCustomer = ds?.isCustomer || false;

      return {
        id:c.id, name:p.name||"Unnamed", domain:p.domain||"",
        ownerId, daysIdle, lastAct,
        touched30:lastAct>=cut30, touched60:lastAct>=cut60, touched90:lastAct>=cut90,
        employees, empBucket:empBucket.key, estTAM, score,
        contacts: +(p.num_associated_contacts||0),
        multiContact: +(p.num_associated_contacts||0) >= 2,
        lifecycle:p.lifecyclestage||"",
        isCustomer,
        // hasOpenDeal uses hs_num_open_deals from company record (reliable, no join needed)
        hasOpenDeal: +(p.hs_num_open_deals||0) > 0,
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[repCompanies, dealData, planDS]);

  // ── Rep-level rollups ─────────────────────────────────────────────────────
  // Activity stats from company enrichment + deal stats from ownerStats
  const repStats = useMemo(()=>{
    // Base structure seeded from reps list
    const stats={};
    reps.forEach(r=>{
      const oid = String(r.hubspotOwnerId);
      // Seed deal stats directly from ownerStats — these are always populated
      const ds = dealData.ownerStats[oid] || {};
      stats[oid]={
        name:r.name, oid, quota:+(r.quota||0),
        // Activity stats (from company enrichment — will be filled below)
        total:0, t30:0, t60:0, t90:0, dark:0, customers:0, multiContact:0,
        totalScore:0, estTAM:0, contacts:0,
        byEmpBucket:{micro:{t:0,t30:0},small:{t:0,t30:0},mid:{t:0,t30:0},ent:{t:0,t30:0}},
        // Deal stats — directly from ownerStats, no company join needed
        openPipe:   ds.openPipe   || 0,
        closedRev:  ds.closedRev  || 0,
        openDeals:  ds.openDeals  || 0,
        wonDeals:   ds.wonDeals   || 0,
        lostDeals:  ds.lostDeals  || 0,
        dealBuckets:ds.dealBuckets|| {xs:0,sm:0,md:0,lg:0},
        _wr:        ds.wr,  // raw win rate before pipeCoverage calc
      };
    });

    // Layer in activity data from enriched companies
    enriched.forEach(c=>{
      const s=stats[c.ownerId]; if(!s) return;
      s.total++;
      if(c.touched30) s.t30++; else s.dark++;
      if(c.touched60) s.t60++;
      if(c.touched90) s.t90++;
      if(c.isCustomer) s.customers++;
      if(c.multiContact) s.multiContact++;
      s.totalScore+=c.score; s.estTAM+=c.estTAM; s.contacts+=c.contacts;
      if(s.byEmpBucket[c.empBucket]){
        s.byEmpBucket[c.empBucket].t++;
        if(c.touched30) s.byEmpBucket[c.empBucket].t30++;
      }
    });

    return Object.values(stats).filter(s=>s.total>0).map(s=>({
      ...s,
      touchRate30:  s.total>0?s.t30/s.total:0,
      touchRate60:  s.total>0?s.t60/s.total:0,
      touchRate90:  s.total>0?s.t90/s.total:0,
      wr:           s._wr,
      avgScore:     s.total>0?Math.round(s.totalScore/s.total):0,
      pipeCoverage: s.quota>0&&s.openPipe>0?s.openPipe/s.quota:null,
      avgContacts:  s.total>0?+(s.contacts/s.total).toFixed(1):0,
      multiContactPct: s.total>0?s.multiContact/s.total:0,
    }));
  },[enriched, reps, dealData]);

  // Team totals — sum deal stats and activity stats separately
  const team = useMemo(()=>{
    const z={total:0,t30:0,t60:0,t90:0,dark:0,customers:0,multiContact:0,contacts:0,
             openPipe:0,closedRev:0,openDeals:0,wonDeals:0,lostDeals:0,
             estTAM:0,totalScore:0};
    repStats.forEach(r=>{
      z.total+=r.total; z.t30+=r.t30; z.t60+=r.t60; z.t90+=r.t90;
      z.dark+=r.dark; z.customers+=r.customers; z.multiContact+=r.multiContact||0;
      z.contacts+=r.contacts||0;
      z.openPipe+=r.openPipe; z.closedRev+=r.closedRev;
      z.openDeals+=r.openDeals; z.wonDeals+=r.wonDeals; z.lostDeals+=r.lostDeals;
      z.estTAM+=r.estTAM; z.totalScore+=r.totalScore;
    });
    const tc=z.wonDeals+z.lostDeals;
    return {...z,
      touchRate30: z.total?z.t30/z.total:0,
      touchRate60: z.total?z.t60/z.total:0,
      touchRate90: z.total?z.t90/z.total:0,
      wr:          tc>0?z.wonDeals/tc:null,
      avgScore:    z.total?Math.round(z.totalScore/z.total):0,
      avgContacts: z.total?+(z.contacts/z.total).toFixed(1):0,
      multiContactPct: z.total?z.multiContact/z.total:0,
    };
  },[repStats]);

  const sorted = useMemo(()=>[...repStats].sort((a,b)=>{
    const av=a[sortBy]??0, bv=b[sortBy]??0;
    return (av-bv)*(sortDir==="asc"?1:-1);
  }),[repStats,sortBy,sortDir]);

  const toggleSort = col => {
    if(sortBy===col) setSortDir(d=>d==="asc"?"desc":"asc");
    else { setSortBy(col); setSortDir("desc"); }
  };
  const SortIco = ({col}) => sortBy===col?(sortDir==="asc"?<ChevronUp size={10}/>:<ChevronDown size={10}/>):null;

  const Th = ({label, col, tip, right=true}) => (
    <Tip text={tip||COL_TIPS[col]||""}>
      <div onClick={col?()=>toggleSort(col):undefined}
        style={{fontSize:9,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:"0.06em",
          textAlign:right?"center":"left",cursor:col?"pointer":"default",
          display:"flex",alignItems:"center",justifyContent:right?"center":"flex-start",
          gap:3,userSelect:"none",whiteSpace:"nowrap",
          borderBottom:"1px dashed rgba(0,0,0,0.2)",paddingBottom:1}}>
        {label}{col&&<SortIco col={col}/>}
      </div>
    </Tip>
  );

  // Empty states
  if(!reps.length) return (
    <div style={{padding:40,textAlign:"center"}}>
      <div style={{fontSize:32,marginBottom:12}}>👥</div>
      <div style={{fontSize:15,fontWeight:700,color:"#111",marginBottom:8}}>No reps configured</div>
      <div style={{fontSize:12,color:"#555"}}>Add your team in <strong>Setup → Quota & Team</strong></div>
    </div>
  );
  if(!companiesLoaded&&!companiesLoading) return (
    <div style={{padding:60,textAlign:"center"}}>
      <div style={{fontSize:44,marginBottom:16}}>🏢</div>
      <div style={{fontSize:15,fontWeight:700,color:"#111",marginBottom:8}}>Load your account base</div>
      <div style={{fontSize:12,color:"#555",maxWidth:380,margin:"0 auto 24px",lineHeight:1.7}}>
        Pulls all HubSpot companies assigned to your reps — shows territory penetration, pipeline by size, and engagement scores.
      </div>
      <button onClick={onLoadCompanies}
        style={{padding:"10px 24px",background:"#CC1A1A",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer"}}>
        Load Accounts from HubSpot
      </button>
    </div>
  );
  if(companiesLoading) return <LoadingPanel count={companiesCount} loadStart={companiesLoadStart}/>;

  const yearOptions = [curYear, curYear-1, curYear-2, curYear-3];
  const COLS = "130px 50px 60px 60px 60px 55px 75px 55px 70px 50px 48px 48px 52px 52px 70px 70px 70px";

  return (
    <div style={{maxWidth:1300,margin:"0 auto",paddingBottom:60}}>

      {/* Controls */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:"0.06em"}}>Calendar Year:</span>
          {yearOptions.map(y=>(
            <button key={y} onClick={()=>setFilterYear(y)}
              style={{padding:"4px 12px",borderRadius:6,
                border:`1px solid ${filterYear===y?"#CC1A1A":"rgba(0,0,0,0.1)"}`,
                background:filterYear===y?"rgba(204,26,26,0.08)":"#fff",
                color:filterYear===y?"#CC1A1A":"#555",
                fontSize:11,fontWeight:filterYear===y?700:400,cursor:"pointer"}}>
              {y}
            </button>
          ))}
          <span style={{fontSize:10,color:"#aaa",marginLeft:4}}>Filters Pipeline, Won & Open Deals by Size columns</span>
        </div>
        <button onClick={onLoadCompanies}
          style={{display:"flex",alignItems:"center",gap:5,padding:"6px 12px",borderRadius:7,
            border:"1px solid rgba(0,0,0,0.1)",background:"#fff",color:"#555",fontSize:11,cursor:"pointer"}}>
          <RefreshCw size={11}/> Refresh
        </button>
      </div>

      {/* Team summary cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(8,1fr)",gap:10,marginBottom:20}}>
        {[
          {l:"Total Accounts",       v:team.total.toLocaleString(),                               c:"#111"},
          {l:"Worked L30",          v:`${team.t30.toLocaleString()} (${pct(team.touchRate30,0)})`,c:clrT(team.touchRate30)},
          {l:"Worked L60",          v:`${team.t60.toLocaleString()} (${pct(team.touchRate60,0)})`,c:clrT(team.touchRate60,0.4,0.7)},
          {l:"Worked L90",          v:`${team.t90.toLocaleString()} (${pct(team.touchRate90,0)})`,c:clrT(team.touchRate90,0.5,0.8)},
          {l:"Open Pipeline",          v:team.openPipe>0?fmtM(team.openPipe):"—",               c:"#7C3AED"},
          {l:`${filterYear} Won`,    v:team.wonDeals>0?team.wonDeals.toLocaleString():"—",        c:"#16A34A"},
          {l:`${filterYear} Win Rate`,v:team.wr!=null?pct(team.wr):"—",                          c:planWR?clrT(team.wr,planWR*0.8,planWR):"#555"},
          {l:"Est. Total TAM",       v:team.estTAM>0?fmtM(team.estTAM):"—",                      c:"#D97706"},
        ].map((k,i)=>(
          <div key={i} style={{background:"#fff",borderRadius:10,border:"1px solid rgba(0,0,0,0.07)",padding:"10px 12px"}}>
            <div style={{fontSize:9,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>{k.l}</div>
            <div style={{fontSize:15,fontWeight:800,color:k.c,fontFamily:"'DM Mono',monospace",lineHeight:1}}>{k.v}</div>
          </div>
        ))}
      </div>

      {/* Main table */}
      <div style={{background:"#fff",borderRadius:12,border:"1px solid rgba(0,0,0,0.07)",overflowX:"auto",minWidth:0}}>
        <div style={{minWidth:1200}}>

        {/* Group headers */}
        <div style={{display:"grid",gridTemplateColumns:COLS,padding:"6px 14px",background:"#F0F0F0",borderBottom:"1px solid rgba(0,0,0,0.06)"}}>
          <div/><div/>
          <div style={{gridColumn:"span 3",textAlign:"center",fontSize:9,fontWeight:700,color:"#CC1A1A",textTransform:"uppercase",letterSpacing:"0.06em",borderLeft:"1px solid rgba(0,0,0,0.06)",paddingLeft:4}}>Account Activity</div>
          <div style={{gridColumn:"span 4",textAlign:"center",fontSize:9,fontWeight:700,color:"#7C3AED",textTransform:"uppercase",letterSpacing:"0.06em",borderLeft:"1px solid rgba(0,0,0,0.06)",paddingLeft:4}}>Pipeline & Bookings (Won = CY{filterYear})</div>
          <div style={{gridColumn:"span 4",textAlign:"center",fontSize:9,fontWeight:700,color:"#D97706",textTransform:"uppercase",letterSpacing:"0.06em",borderLeft:"1px solid rgba(0,0,0,0.06)",paddingLeft:4}}>Open Deals by Size · CY{filterYear}</div>
          <div style={{gridColumn:"span 2",textAlign:"center",fontSize:9,fontWeight:700,color:"#555",textTransform:"uppercase",letterSpacing:"0.06em",borderLeft:"1px solid rgba(0,0,0,0.06)",paddingLeft:4}}>Territory</div>
          <div style={{gridColumn:"span 1",textAlign:"center",fontSize:9,fontWeight:700,color:"#DC2626",textTransform:"uppercase",letterSpacing:"0.06em",borderLeft:"1px solid rgba(0,0,0,0.06)",paddingLeft:4}}>CRM Quality</div>
        </div>

        {/* Column headers — all with dashed underline + tooltip */}
        <div style={{display:"grid",gridTemplateColumns:COLS,padding:"7px 14px",background:"#F8F8F8",borderBottom:"2px solid rgba(0,0,0,0.07)"}}>
          <Th label="Rep / Territory" right={false} tip="Sales rep. Click row to expand dark accounts."/>
          <Th label="Accts"   col="total"/>
          <Th label="L30 %"   col="touchRate30"/>
          <Th label="L60 %"   col="touchRate60"/>
          <Th label="L90 %"   col="touchRate90"/>
          <Th label="# Open"  col="openDeals"/>
          <Th label="Open $"  col="openPipe"/>
          <Th label="# Won"   col="wonDeals"/>
          <Th label="Won $"   col="closedRev"/>
          <Th label="WR %"    col="wr"/>
          {DEAL_BUCKETS.map(b=><Th key={b.key} label={b.label} tip={COL_TIPS[b.key]}/>)}
          <Th label="Est TAM" col="estTAM"/>
          <Th label="Mktg Score" col="avgScore"/>
          <Th label="Avg Cts" col="avgContacts" tip="Avg contacts per account. Accounts with 2+ contacts = buyer + champion coverage."/>
        </div>

        {/* Rep rows */}
        {sorted.map((rep,ri)=>{
          const isExp    = expandedRep===rep.oid;
          const repAccts = enriched.filter(c=>c.ownerId===rep.oid);
          const darkAccts= repAccts.filter(c=>!c.touched30).sort((a,b)=>b.daysIdle-a.daysIdle);
          return (
            <React.Fragment key={rep.oid}>
              <div onClick={()=>setExpandedRep(isExp?null:rep.oid)}
                style={{display:"grid",gridTemplateColumns:COLS,padding:"9px 14px",
                  borderBottom:"1px solid rgba(0,0,0,0.04)",
                  background:isExp?"rgba(204,26,26,0.02)":ri%2?"rgba(0,0,0,0.01)":"#fff",
                  cursor:"pointer",alignItems:"center"}}
                onMouseEnter={e=>e.currentTarget.style.background="rgba(204,26,26,0.03)"}
                onMouseLeave={e=>e.currentTarget.style.background=isExp?"rgba(204,26,26,0.02)":ri%2?"rgba(0,0,0,0.01)":"#fff"}>

                <div style={{display:"flex",alignItems:"center",gap:5}}>
                  <span style={{color:isExp?"#CC1A1A":"#aaa"}}>{isExp?<ChevronUp size={11}/>:<ChevronDown size={11}/>}</span>
                  <span style={{fontSize:12,fontWeight:700,color:"#111"}}>{rep.name}</span>
                </div>

                <div style={{textAlign:"center",fontSize:12,color:"#555"}}>{rep.total}</div>

                {/* Activity — always rolling, not year-filtered */}
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:12,fontWeight:700,color:clrT(rep.touchRate30)}}>{pct(rep.touchRate30,0)}</div>
                  <div style={{fontSize:9,color:"#aaa"}}>{rep.t30}/{rep.total}</div>
                </div>
                <div style={{textAlign:"center",fontSize:11,color:clrT(rep.touchRate60,0.4,0.7)}}>{pct(rep.touchRate60,0)}</div>
                <div style={{textAlign:"center",fontSize:11,color:clrT(rep.touchRate90,0.5,0.8)}}>{pct(rep.touchRate90,0)}</div>

                {/* Pipeline & Bookings — year-filtered */}
                <div style={{textAlign:"center",fontSize:11,color:"#7C3AED",fontWeight:rep.openDeals>0?600:400}}>{fmtN(rep.openDeals)}</div>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:11,fontFamily:"'DM Mono',monospace",color:"#111",fontWeight:600}}>{rep.openPipe>0?fmtM(rep.openPipe):"—"}</div>
                  {rep.pipeCoverage!=null&&rep.pipeCoverage>0&&<div style={{fontSize:9,color:rep.pipeCoverage>=3?"#16A34A":rep.pipeCoverage>=2?"#D97706":"#DC2626"}}>{rep.pipeCoverage.toFixed(1)}x</div>}
                </div>
                <div style={{textAlign:"center",fontSize:11,color:"#16A34A",fontWeight:rep.wonDeals>0?600:400}}>{fmtN(rep.wonDeals)}</div>
                <div style={{textAlign:"center",fontSize:11,fontFamily:"'DM Mono',monospace",color:"#16A34A",fontWeight:600}}>{rep.closedRev>0?fmtM(rep.closedRev):"—"}</div>
                <div style={{textAlign:"center",fontSize:12,fontWeight:700,
                  color:rep.wr!=null?(planWR?clrT(rep.wr,planWR*0.8,planWR):"#555"):"#888",
                  fontFamily:"'DM Mono',monospace"}}>
                  {rep.wr!=null?pct(rep.wr):"—"}
                </div>

                {/* Open deals by size — year-filtered */}
                {DEAL_BUCKETS.map(b=>(
                  <div key={b.key} style={{textAlign:"center",fontSize:11,
                    color:rep.dealBuckets[b.key]>0?"#D97706":"#ddd",
                    fontWeight:rep.dealBuckets[b.key]>0?600:400}}>
                    {rep.dealBuckets[b.key]>0?rep.dealBuckets[b.key]:"—"}
                  </div>
                ))}

                <div style={{textAlign:"center",fontSize:10,color:"#D97706",fontFamily:"'DM Mono',monospace"}}>{rep.estTAM>0?fmtM(rep.estTAM):"—"}</div>
                <div style={{textAlign:"center",fontSize:12,fontWeight:rep.avgScore>0?700:400,
                  color:rep.avgScore>50?"#16A34A":rep.avgScore>20?"#D97706":"#888"}}>
                  {rep.avgScore>0?rep.avgScore:"—"}
                </div>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:12,fontWeight:700,
                    color:rep.avgContacts>=2?"#16A34A":rep.avgContacts>=1?"#D97706":"#DC2626"}}>
                    {rep.avgContacts>0?rep.avgContacts:"—"}
                  </div>
                  {rep.total>0&&<div style={{fontSize:9,color:"#aaa"}}>{Math.round(rep.multiContactPct*100)}% 2+</div>}
                </div>
              </div>

              {/* Expanded detail */}
              {isExp&&(
                <div style={{background:"rgba(204,26,26,0.015)",borderBottom:"2px solid rgba(204,26,26,0.07)"}}>
                  <div style={{padding:"10px 20px 6px",display:"flex",gap:16,flexWrap:"wrap",borderBottom:"1px solid rgba(0,0,0,0.04)"}}>
                    <span style={{fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:"0.06em",alignSelf:"center"}}>By Company Size:</span>
                    {EMP_BUCKETS.map(b=>{
                      const bk=rep.byEmpBucket[b.key];
                      return bk.t>0?(
                        <div key={b.key} style={{fontSize:10,color:"#555"}}>
                          <span style={{fontWeight:700,color:"#111"}}>{b.label}</span>{" "}{bk.t} accts{" "}
                          <span style={{color:clrT(bk.t>0?bk.t30/bk.t:0)}}>{pct(bk.t>0?bk.t30/bk.t:0,0)} touched</span>
                        </div>
                      ):null;
                    })}
                    <div style={{marginLeft:"auto",fontSize:10,color:"#555"}}>
                      Avg contacts/acct: <strong>{rep.contacts>0&&rep.total>0?(rep.contacts/rep.total).toFixed(1):"—"}</strong>
                      {" · "}Customers: <strong style={{color:"#16A34A"}}>{rep.customers}</strong>
                      {" · "}Est TAM: <strong style={{color:"#D97706"}}>{rep.estTAM>0?fmtM(rep.estTAM):"—"}</strong>
                    </div>
                  </div>
                  {darkAccts.length>0&&(
                    <div style={{padding:"8px 16px 12px"}}>
                      <div style={{fontSize:9,fontWeight:700,color:"#DC2626",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>
                        🔴 No Activity in 30+ Days ({darkAccts.length} accounts)
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6}}>
                        {darkAccts.slice(0,16).map((c,ci)=>(
                          <div key={ci} style={{background:"#fff",borderRadius:7,border:"1px solid rgba(220,38,38,0.1)",
                            padding:"6px 10px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                            <div style={{overflow:"hidden",marginRight:6}}>
                              <div style={{fontSize:11,fontWeight:600,color:"#111",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.name}</div>
                              <div style={{fontSize:9,color:"#888"}}>{c.employees>0?`${c.employees.toLocaleString()} emp`:""}{c.lifecycle?` · ${c.lifecycle}`:""}</div>
                            </div>
                            <div style={{textAlign:"right",flexShrink:0}}>
                              <div style={{fontSize:10,fontWeight:700,color:"#DC2626"}}>{c.daysIdle<999?`${c.daysIdle}d`:"Never"}</div>
                              {c.hasOpenDeal&&<div style={{fontSize:9,color:"#D97706"}}>Open deal</div>}
                              {c.isCustomer&&<div style={{fontSize:9,color:"#7C3AED"}}>Customer</div>}
                            </div>
                          </div>
                        ))}
                        {darkAccts.length>16&&<div style={{gridColumn:"1/-1",fontSize:10,color:"#888",padding:"4px 10px"}}>+{darkAccts.length-16} more dark accounts</div>}
                      </div>
                    </div>
                  )}
                  {/* Contact depth breakdown */}
                  {(() => {
                    const none   = repAccts.filter(c=>c.contacts===0);
                    const one    = repAccts.filter(c=>c.contacts===1);
                    const multi  = repAccts.filter(c=>c.contacts>=2);
                    if (!repAccts.length) return null;
                    return (
                      <div style={{padding:"8px 16px 12px",borderTop:"1px solid rgba(0,0,0,0.04)"}}>
                        <div style={{fontSize:9,fontWeight:700,color:"#7C3AED",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>
                          👥 Contact Depth
                        </div>
                        <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:10}}>
                          {[
                            {label:"0 contacts",  count:none.length,  color:"#DC2626", tip:"No buyer on record"},
                            {label:"1 contact",   count:one.length,   color:"#D97706", tip:"Single-threaded"},
                            {label:"2+ contacts", count:multi.length, color:"#16A34A", tip:"Buyer + champion"},
                          ].map((b,i)=>(
                            <div key={i} style={{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",
                              background:`${b.color}10`,border:`1px solid ${b.color}25`,borderRadius:8}}>
                              <span style={{fontSize:16,fontWeight:800,color:b.color,fontFamily:"'DM Mono',monospace"}}>{b.count}</span>
                              <div>
                                <div style={{fontSize:11,fontWeight:600,color:b.color}}>{b.label}</div>
                                <div style={{fontSize:9,color:"#888"}}>{b.tip}</div>
                              </div>
                            </div>
                          ))}
                          <div style={{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",
                            background:"rgba(0,0,0,0.03)",border:"1px solid rgba(0,0,0,0.07)",borderRadius:8}}>
                            <span style={{fontSize:16,fontWeight:800,color:"#111",fontFamily:"'DM Mono',monospace"}}>{rep.avgContacts}</span>
                            <div>
                              <div style={{fontSize:11,fontWeight:600,color:"#111"}}>avg / acct</div>
                              <div style={{fontSize:9,color:"#888"}}>{Math.round(rep.multiContactPct*100)}% have 2+</div>
                            </div>
                          </div>
                        </div>
                        {none.length>0&&(
                          <div>
                            <div style={{fontSize:9,color:"#DC2626",fontWeight:700,marginBottom:4}}>Accounts with no contacts:</div>
                            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                              {none.slice(0,12).map((c,ci)=>(
                                <div key={ci} style={{fontSize:10,padding:"3px 8px",background:"rgba(220,38,38,0.06)",
                                  border:"1px solid rgba(220,38,38,0.12)",borderRadius:5,color:"#111"}}>
                                  {c.name}{c.hasOpenDeal&&<span style={{color:"#D97706"}}> ⚠</span>}
                                </div>
                              ))}
                              {none.length>12&&<div style={{fontSize:10,color:"#aaa",padding:"3px 8px"}}>+{none.length-12} more</div>}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </React.Fragment>
          );
        })}

        {/* Team totals row */}
        <div style={{display:"grid",gridTemplateColumns:COLS,padding:"9px 14px",
          background:"#F0F0F0",borderTop:"2px solid rgba(0,0,0,0.08)",alignItems:"center"}}>
          <div style={{fontSize:11,fontWeight:800,color:"#111",textTransform:"uppercase",letterSpacing:"0.05em"}}>Team Total</div>
          <div style={{textAlign:"center",fontSize:12,fontWeight:700,color:"#111"}}>{team.total}</div>
          <div style={{textAlign:"center",fontSize:12,fontWeight:700,color:clrT(team.touchRate30)}}>{pct(team.touchRate30,0)}</div>
          <div style={{textAlign:"center",fontSize:11,color:clrT(team.touchRate60,0.4,0.7)}}>{pct(team.touchRate60,0)}</div>
          <div style={{textAlign:"center",fontSize:11,color:clrT(team.touchRate90,0.5,0.8)}}>{pct(team.touchRate90,0)}</div>
          <div style={{textAlign:"center",fontSize:11,fontWeight:700,color:"#7C3AED"}}>{fmtN(team.openDeals)}</div>
          <div style={{textAlign:"center",fontSize:11,fontFamily:"'DM Mono',monospace",fontWeight:700}}>{team.openPipe>0?fmtM(team.openPipe):"—"}</div>
          <div style={{textAlign:"center",fontSize:11,fontWeight:700,color:"#16A34A"}}>{fmtN(team.wonDeals)}</div>
          <div style={{textAlign:"center",fontSize:11,fontFamily:"'DM Mono',monospace",fontWeight:700,color:"#16A34A"}}>{team.closedRev>0?fmtM(team.closedRev):"—"}</div>
          <div style={{textAlign:"center",fontSize:12,fontWeight:700,
            color:team.wr!=null?clrT(team.wr,planWR*0.8,planWR):"#888",
            fontFamily:"'DM Mono',monospace"}}>{team.wr!=null?pct(team.wr):"—"}</div>
          {DEAL_BUCKETS.map(b=>{
            const total=repStats.reduce((a,r)=>a+(r.dealBuckets[b.key]||0),0);
            return <div key={b.key} style={{textAlign:"center",fontSize:11,color:"#D97706",fontWeight:700}}>{total>0?total:"—"}</div>;
          })}
          <div style={{textAlign:"center",fontSize:10,color:"#D97706",fontFamily:"'DM Mono',monospace"}}>{team.estTAM>0?fmtM(team.estTAM):"—"}</div>
          <div style={{textAlign:"center",fontSize:12,fontWeight:700,color:team.avgScore>50?"#16A34A":team.avgScore>20?"#D97706":"#888"}}>{team.avgScore>0?team.avgScore:"—"}</div>
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:12,fontWeight:700,
              color:team.avgContacts>=2?"#16A34A":team.avgContacts>=1?"#D97706":"#DC2626"}}>
              {team.avgContacts>0?team.avgContacts:"—"}
            </div>
            {team.total>0&&<div style={{fontSize:9,color:"#aaa"}}>{Math.round(team.multiContactPct*100)}% 2+</div>}
          </div>
        </div>
      </div>{/* end minWidth inner */}
      </div>{/* end table container */}

      <div style={{marginTop:8,fontSize:10,color:"#aaa",textAlign:"right",lineHeight:1.8}}>
        {companies.length.toLocaleString()} accounts loaded ·
        Worked cols = meaningful touches (call/meeting/note only, emails excluded) · rolling 30/60/90 days ·
        Open Pipeline = all current deals · Won filtered to CY{filterYear} ·
        Est. TAM = Plan ADS × size multiplier ·
        Marketing Score = hubspot_score property · Hover column headers for definitions
      </div>

      {/* ── UNASSIGNED TERRITORY ─────────────────────────────────────────── */}
      {(() => {
        const unowned = companies.filter(c => !c.properties?.hubspot_owner_id);
        if (!unowned.length) return null;

        // Break down by lifecycle stage
        const byStage = {};
        unowned.forEach(c => {
          const stage = c.properties?.lifecyclestage || "unknown";
          if (!byStage[stage]) byStage[stage] = { count:0, companies:[] };
          byStage[stage].count++;
          byStage[stage].companies.push(c);
        });

        // Stage display config
        const STAGE_CONFIG = {
          lead:           { label:"Lead",           color:"#7C3AED", priority:1 },
          marketingqualifiedlead: { label:"MQL",    color:"#CC1A1A", priority:2 },
          salesqualifiedlead:     { label:"SQL",    color:"#DC2626", priority:3 },
          opportunity:    { label:"Opportunity",    color:"#D97706", priority:4 },
          customer:       { label:"Customer",       color:"#16A34A", priority:5 },
          evangelist:     { label:"Evangelist",     color:"#0891B2", priority:6 },
          subscriber:     { label:"Subscriber",     color:"#555",    priority:7 },
          other:          { label:"Other",           color:"#888",    priority:8 },
          unknown:        { label:"No Stage Set",   color:"#ccc",    priority:9 },
        };

        const stageSummary = Object.entries(byStage)
          .map(([stage, data]) => ({
            stage,
            ...data,
            config: STAGE_CONFIG[stage] || { label: stage, color:"#888", priority:99 }
          }))
          .sort((a,b) => a.config.priority - b.config.priority);

        const highPriority = unowned.filter(c => {
          const s = c.properties?.lifecyclestage || "";
          return ["marketingqualifiedlead","salesqualifiedlead","opportunity","customer"].includes(s);
        });

        return (
          <div style={{marginTop:24}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
              <div>
                <div style={{fontSize:14,fontWeight:800,color:"#111"}}>⚠️ Unassigned Territory</div>
                <div style={{fontSize:11,color:"#888",marginTop:2}}>
                  {unowned.length.toLocaleString()} of {companies.length.toLocaleString()} loaded accounts have no owner assigned ({Math.round(unowned.length/companies.length*100)}%)
                  {highPriority.length > 0 && <span style={{color:"#DC2626",fontWeight:700}}> · {highPriority.length} are MQL/SQL/Opportunity/Customer</span>}
                </div>
              </div>
            </div>

            {/* Stage breakdown */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:10,marginBottom:16}}>
              {stageSummary.map(s => (
                <div key={s.stage} style={{background:"#fff",borderRadius:10,border:`1px solid ${s.config.color}25`,padding:"12px 14px"}}>
                  <div style={{fontSize:10,fontWeight:700,color:s.config.color,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>{s.config.label}</div>
                  <div style={{fontSize:20,fontWeight:900,color:s.config.color,fontFamily:"'DM Mono',monospace"}}>{s.count.toLocaleString()}</div>
                  <div style={{fontSize:10,color:"#aaa",marginTop:2}}>{Math.round(s.count/unowned.length*100)}% of unowned</div>
                </div>
              ))}
            </div>

            {/* High-priority unowned accounts */}
            {highPriority.length > 0 && (
              <div style={{background:"#fff",borderRadius:12,border:"1px solid rgba(220,38,38,0.15)",overflow:"hidden"}}>
                <div style={{padding:"10px 16px",background:"rgba(220,38,38,0.04)",borderBottom:"1px solid rgba(220,38,38,0.08)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{fontSize:12,fontWeight:700,color:"#DC2626"}}>🚨 High-Priority Unowned — MQL / SQL / Opportunity / Customer</div>
                  <div style={{fontSize:11,color:"#DC2626",fontFamily:"'DM Mono',monospace",fontWeight:700}}>{highPriority.length} accounts</div>
                </div>
                {/* Header */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 120px 100px 120px 100px",padding:"6px 16px",background:"#F8F8F8",borderBottom:"1px solid rgba(0,0,0,0.05)"}}>
                  {["Company","Lifecycle Stage","Employees","Industry","Last Activity"].map((h,i)=>(
                    <div key={i} style={{fontSize:9,fontWeight:700,color:"#aaa",textTransform:"uppercase",letterSpacing:"0.06em"}}>{h}</div>
                  ))}
                </div>
                {highPriority.slice(0,15).map((c,i) => {
                  const p = c.properties || {};
                  const stage = p.lifecyclestage || "—";
                  const stageCfg = STAGE_CONFIG[stage] || {label:stage,color:"#888"};
                  const lastAct = p.notes_last_activity_date || p.hs_lastmodifieddate;
                  const daysAgo = lastAct ? Math.round((Date.now()-new Date(lastAct).getTime())/86400000) : null;
                  return (
                    <div key={i} style={{display:"grid",gridTemplateColumns:"1fr 120px 100px 120px 100px",padding:"9px 16px",
                      borderBottom:i<Math.min(highPriority.length,15)-1?"1px solid rgba(0,0,0,0.04)":"none",
                      background:"#fff",alignItems:"center"}}>
                      <div style={{fontSize:12,fontWeight:600,color:"#111",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name||"Unnamed"}</div>
                      <div><span style={{fontSize:10,fontWeight:700,color:stageCfg.color,background:`${stageCfg.color}15`,borderRadius:5,padding:"2px 7px"}}>{stageCfg.label}</span></div>
                      <div style={{fontSize:11,color:"#555"}}>{p.numberofemployees?Number(p.numberofemployees).toLocaleString():"—"}</div>
                      <div style={{fontSize:11,color:"#555",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.industry||"—"}</div>
                      <div style={{fontSize:11,color:daysAgo!=null&&daysAgo>30?"#DC2626":daysAgo!=null&&daysAgo>14?"#D97706":"#555"}}>
                        {daysAgo!=null?`${daysAgo}d ago`:"Never"}
                      </div>
                    </div>
                  );
                })}
                {highPriority.length>15&&(
                  <div style={{padding:"8px 16px",fontSize:11,color:"#aaa",borderTop:"1px solid rgba(0,0,0,0.04)"}}>
                    +{highPriority.length-15} more unassigned high-priority accounts
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default AccountCoverage;
